import {
  Award,
  BarChart3,
  Brain,
  Calendar,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Globe,
  Hash,
  RefreshCw,
  Target,
  TrendingUp,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import apiService from "../../shared/apiService";
import { API_ENDPOINTS } from "../../shared/constants";
import { getPlatformName } from "../../utils/platformIcons";
import Button from "../ui/Button";
import Modal from "../ui/Modal";

interface AnalyticsData {
  overview: {
    total_posts: number;
    total_impressions: number;
    total_engagement: number;
    avg_engagement_rate: number;
    total_clicks: number;
    total_conversions: number;
    conversion_rate: number;
  };
  performance_by_platform: Array<{
    platform: string;
    posts: number;
    impressions: number;
    engagement_rate: number;
    clicks: number;
    conversions: number;
  }>;
  performance_by_content_type: Array<{
    content_type: string;
    posts: number;
    avg_engagement_rate: number;
    avg_clicks: number;
  }>;
  top_performing_posts: Array<{
    id: string;
    title: string;
    platform: string;
    engagement_rate: number;
    impressions: number;
    created_at: string;
  }>;
  engagement_trends: Array<{
    date: string;
    engagement_rate: number;
    impressions: number;
  }>;
  hashtag_performance: Array<{
    hashtag: string;
    usage_count: number;
    avg_engagement_rate: number;
  }>;
  timing_analysis: Array<{
    hour: number;
    avg_engagement_rate: number;
    posts_count: number;
  }>;
  learning_insights: {
    total_samples: number;
    learned_patterns: number;
    recommendations_generated: number;
    model_status: {
      engagement_model: boolean;
      conversion_model: boolean;
    };
    top_performing_patterns: Array<{
      pattern_id: string;
      type: string;
      performance_score: number;
      confidence: number;
      trend: string;
    }>;
  };
  ab_tests: Array<{
    test_id: string;
    test_name: string;
    status: string;
    start_date: string;
    variants_count: number;
    total_impressions: number;
  }>;
}

interface AdvancedAnalyticsDashboardProps {
  className?: string;
}

const AdvancedAnalyticsDashboard: React.FC<AdvancedAnalyticsDashboardProps> = ({
  className = "",
}) => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTimeRange, setSelectedTimeRange] = useState("30d");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(["overview"]),
  );
  const [showABTestModal, setShowABTestModal] = useState(false);
  const [showLearningModal, setShowLearningModal] = useState(false);

  const loadAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await apiService.post(
        API_ENDPOINTS.ANALYTICS.ADVANCED_DASHBOARD,
        {
          time_range: selectedTimeRange,
          platform: selectedPlatform,
        },
      );
      if (result.success) {
        setData(result.data);
      } else {
        throw new Error(result.error || "Erro desconhecido");
      }
    } catch (err: any) {
      setError(err.message || "Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [selectedTimeRange, selectedPlatform]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const formatPercentage = (num: number) => {
    return `${(num * 100).toFixed(1)}%`;
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case "improving":
        return <TrendingUp className="h-4 w-4 text-green-500" />;
      case "declining":
        return <TrendingUp className="h-4 w-4 text-red-500 rotate-180" />;
      default:
        return <TrendingUp className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "paused":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Carregando analytics...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
        <div className="text-center text-red-600">
          <p>Erro ao carregar dados: {error}</p>
          <Button
            onClick={loadAnalyticsData}
            className="mt-4"
            variant="secondary"
          >
            Tentar Novamente
          </Button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
        <div className="text-center text-gray-600">
          <p>Nenhum dado disponível</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header com controles */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold flex items-center">
            <BarChart3 className="h-8 w-8 mr-3 text-blue-600" />
            Dashboard Avançado de Analytics
          </h1>
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => setShowABTestModal(true)}
              variant="secondary"
              icon={<Target className="h-4 w-4" />}
            >
              A/B Tests
            </Button>
            <Button
              onClick={() => setShowLearningModal(true)}
              variant="secondary"
              icon={<Brain className="h-4 w-4" />}
            >
              IA Learning
            </Button>
            <Button
              onClick={loadAnalyticsData}
              variant="secondary"
              icon={<RefreshCw className="h-4 w-4" />}
            >
              Atualizar
            </Button>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Calendar className="h-4 w-4 text-gray-500" />
            <select
              value={selectedTimeRange}
              onChange={(e) => setSelectedTimeRange(e.target.value)}
              className="border border-purple-300 rounded-sm px-3 py-1"
            >
              <option value="7d">Últimos 7 dias</option>
              <option value="30d">Últimos 30 dias</option>
              <option value="90d">Últimos 90 dias</option>
              <option value="1y">Último ano</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <Globe className="h-4 w-4 text-gray-500" />
            <select
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              className="border border-purple-300 rounded-sm px-3 py-1"
            >
              <option value="all">Todas as plataformas</option>
              <option value="linkedin">LinkedIn</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
            </select>
          </div>
        </div>
      </div>

      {/* Overview */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("overview")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Eye className="h-6 w-6 mr-2 text-blue-600" />
              Visão Geral
            </h2>
            {expandedSections.has("overview") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("overview") && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {formatNumber(data.overview.total_posts)}
                </div>
                <div className="text-sm text-gray-600">Total de Posts</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {formatNumber(data.overview.total_impressions)}
                </div>
                <div className="text-sm text-gray-600">Impressões</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600">
                  {formatPercentage(data.overview.avg_engagement_rate)}
                </div>
                <div className="text-sm text-gray-600">Taxa de Engajamento</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600">
                  {formatPercentage(data.overview.conversion_rate)}
                </div>
                <div className="text-sm text-gray-600">Taxa de Conversão</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance por Plataforma */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("platforms")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Globe className="h-6 w-6 mr-2 text-green-600" />
              Performance por Plataforma
            </h2>
            {expandedSections.has("platforms") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("platforms") && (
          <div className="p-6">
            <div className="space-y-4">
              {data.performance_by_platform.map((platform, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-300"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Globe className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 capitalize">
                        {getPlatformName(platform.platform)}
                      </div>
                      <div className="text-sm text-gray-600">
                        {platform.posts} posts
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      {formatPercentage(platform.engagement_rate)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatNumber(platform.impressions)} impressões
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Top Posts */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("top_posts")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Award className="h-6 w-6 mr-2 text-yellow-600" />
              Top Posts
            </h2>
            {expandedSections.has("top_posts") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("top_posts") && (
          <div className="p-6">
            <div className="space-y-3">
              {data.top_performing_posts.map((post, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 border border-purple-300 rounded-lg"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-yellow-600">
                        #{index + 1}
                      </span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {post.title}
                      </div>
                      <div className="text-sm text-gray-600 capitalize">
                        {getPlatformName(post.platform)}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-bold text-green-600">
                      {formatPercentage(post.engagement_rate)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatNumber(post.impressions)} impressões
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Hashtag Performance */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("hashtags")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Hash className="h-6 w-6 mr-2 text-purple-600" />
              Performance de Hashtags
            </h2>
            {expandedSections.has("hashtags") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("hashtags") && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.hashtag_performance.map((hashtag, index) => (
                <div
                  key={index}
                  className="p-4 bg-purple-50 rounded-lg border border-purple-300"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-900">
                      {hashtag.hashtag}
                    </span>
                    <span className="text-sm text-gray-600">
                      {hashtag.usage_count} usos
                    </span>
                  </div>
                  <div className="text-lg font-bold text-purple-600">
                    {formatPercentage(hashtag.avg_engagement_rate)}
                  </div>
                  <div className="text-sm text-gray-600">Engajamento médio</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Timing Analysis */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("timing")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Clock className="h-6 w-6 mr-2 text-indigo-600" />
              Análise de Timing
            </h2>
            {expandedSections.has("timing") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("timing") && (
          <div className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {data.timing_analysis.map((timing, index) => (
                <div
                  key={index}
                  className="text-center p-3 bg-purple-50 rounded-lg border border-purple-300"
                >
                  <div className="text-lg font-bold text-indigo-600">
                    {timing.hour}:00
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatPercentage(timing.avg_engagement_rate)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {timing.posts_count} posts
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Learning Insights */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("learning")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Brain className="h-6 w-6 mr-2 text-pink-600" />
              Insights de IA
            </h2>
            {expandedSections.has("learning") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("learning") && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  Estatísticas de Aprendizado
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amostras de dados:</span>
                    <span className="font-medium">
                      {data.learning_insights.total_samples}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Padrões aprendidos:</span>
                    <span className="font-medium">
                      {data.learning_insights.learned_patterns}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Recomendações geradas:
                    </span>
                    <span className="font-medium">
                      {data.learning_insights.recommendations_generated}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-4">
                  Top Padrões
                </h3>
                <div className="space-y-2">
                  {data.learning_insights.top_performing_patterns
                    .slice(0, 3)
                    .map((pattern, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-purple-50 rounded-sm border border-purple-300"
                      >
                        <div className="flex items-center space-x-2">
                          {getTrendIcon(pattern.trend)}
                          <span className="text-sm font-medium">
                            {pattern.type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatPercentage(pattern.performance_score)}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* A/B Tests */}
      <div className="bg-white rounded-lg shadow-sm">
        <div
          className="p-6 border-b border-purple-300 cursor-pointer"
          onClick={() => toggleSection("ab_tests")}
        >
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold flex items-center">
              <Target className="h-6 w-6 mr-2 text-red-600" />
              Testes A/B
            </h2>
            {expandedSections.has("ab_tests") ? (
              <ChevronUp className="h-5 w-5" />
            ) : (
              <ChevronDown className="h-5 w-5" />
            )}
          </div>
        </div>
        {expandedSections.has("ab_tests") && (
          <div className="p-6">
            {data.ab_tests.length > 0 ? (
              <div className="space-y-3">
                {data.ab_tests.map((test, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 border border-purple-300 rounded-lg"
                  >
                    <div>
                      <div className="font-medium text-gray-900">
                        {test.test_name}
                      </div>
                      <div className="text-sm text-gray-600">
                        {test.variants_count} variações •{" "}
                        {formatNumber(test.total_impressions)} impressões
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(test.status)}`}
                      >
                        {test.status}
                      </span>
                      <Button size="sm" variant="secondary">
                        Ver Detalhes
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-600 py-8">
                <Target className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                <p>Nenhum teste A/B ativo</p>
                <Button
                  onClick={() => setShowABTestModal(true)}
                  className="mt-4"
                  variant="primary"
                >
                  Criar Primeiro Teste
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modais */}
      {showABTestModal && (
        <Modal
          open={showABTestModal}
          onClose={() => setShowABTestModal(false)}
          title="Gerenciar Testes A/B"
          maxWidth="max-w-4xl"
        >
          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Sistema de A/B Testing automático em desenvolvimento...
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setShowABTestModal(false)}>Fechar</Button>
            </div>
          </div>
        </Modal>
      )}

      {showLearningModal && (
        <Modal
          open={showLearningModal}
          onClose={() => setShowLearningModal(false)}
          title="Sistema de Aprendizado IA"
          maxWidth="max-w-4xl"
        >
          <div className="p-6">
            <p className="text-gray-600 mb-4">
              Sistema de aprendizado contínuo em desenvolvimento...
            </p>
            <div className="flex justify-end">
              <Button onClick={() => setShowLearningModal(false)}>
                Fechar
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdvancedAnalyticsDashboard;
